import { CloseCircleOutlined } from "@ant-design/icons"
import { Select } from "antd"
import type { BaseOptionType } from "antd/es/select"
import React, { useEffect, useMemo, useState } from "react"

import { useAppSelector } from "../../hooks/use-app-selector"
import { cn } from "../../utils/cn"
import { filterOptionsByVisibility } from "../../utils/filterOptionsByVisibility.ts"
import { capitalizeOptionLabels } from "../../utils/select/capitalize-option-labels.ts"
import {
  type FallbackLabels,
  mergeSelectedIntoOptions,
  modeAcceptsFreeText
} from "../../utils/select/resolve-options.ts"
import { capitalizeFirstLetter } from "../../utils/text/capitalize-first-letter.ts"

const EMPTY_SET: ReadonlySet<string> = new Set()

/**
 * Every value already known as an option, stringified — never re-capitalize
 * those. Stringified to match `mergeSelectedIntoOptions`'s own `present` set
 * in resolve-options.ts (same option-list walk), so a numeric option value
 * and a same-looking typed string agree on identity between the two.
 */
function knownOptionValues(options: BaseOptionType[] | undefined): Set<string> {
  const values = new Set<string>()
  for (const option of options ?? []) {
    if (Array.isArray(option?.options)) {
      for (const child of option.options) values.add(String(child?.value))
    } else {
      values.add(String(option?.value))
    }
  }
  return values
}

interface SelectComponentProps extends React.ComponentProps<typeof Select> {
  optionFilterType?: string
  fallbackLabels?: FallbackLabels
  fallbackLabel?: string
  /** Opts free text typed in tags/combobox mode out of auto-capitalization. */
  disableAutoCapitalize?: boolean
  /** Opts the rendered option labels out of auto-capitalization. */
  disableLabelCapitalize?: boolean
}

export const SelectComponent = React.forwardRef<any, SelectComponentProps>(
  (
    {
      className,
      options,
      optionFilterType,
      onChange,
      allowClear = true,
      fallbackLabels,
      fallbackLabel,
      disableAutoCapitalize,
      disableLabelCapitalize,
      ...props
    },
    ref
  ) => {
    const isDisabledTransition = useAppSelector(
      (state) => state.nav.disabledTransition
    )
    const isMobile = useAppSelector((state) => state.nav.isMobile)
    const [open, setOpen] = useState<boolean>(false)
    const acceptsFreeText = modeAcceptsFreeText(props?.mode)
    // Tags mode only, not combobox — see the comment on capitalizeCommittedValue.
    const capitalizesFreeText = props?.mode === "tags"

    // Visibility filtering can strip the very option a stored value points at,
    // so the selected values are merged back in after it runs. Memoised: a fresh
    // array on every render makes antd re-flatten its options each time.
    const filteredOptions = useMemo(() => {
      const visible = optionFilterType
        ? filterOptionsByVisibility(options, optionFilterType)
        : options
      const resolved = acceptsFreeText
        ? visible
        : mergeSelectedIntoOptions(
            visible,
            props?.value,
            fallbackLabels,
            fallbackLabel
          )
      return disableLabelCapitalize
        ? resolved
        : capitalizeOptionLabels(resolved)
    }, [
      options,
      optionFilterType,
      acceptsFreeText,
      props?.value,
      fallbackLabels,
      fallbackLabel,
      disableLabelCapitalize
    ])

    // Only tags mode ever reads this (see capitalizeCommittedValue below) —
    // skip building it for every other, far more common Select.
    // From the visible/filtered options, not the raw `options` prop: a value
    // an `optionFilterType` hides from this Select isn't one the user could
    // have picked, so free text matching it is still genuinely new typing.
    const knownValues = useMemo(
      () =>
        capitalizesFreeText ? knownOptionValues(filteredOptions) : EMPTY_SET,
      [capitalizesFreeText, filteredOptions]
    )

    const handleOpenChange = (open: boolean) => {
      setOpen(open)

      if (open) {
        document.body.style.overflow = "hidden"
      } else {
        document.body.style.overflow = "unset"
      }

      props?.onOpenChange?.(open)
    }

    // antd calls `onChange(value, option)`, and callers hang their own data off
    // the option to read back here — the calendar's lead/project pickers carry
    // the address that fills the meeting Location that way. Forward both, or
    // that data is silently unreachable through this wrapper.
    //
    // Free text is capitalized here, at commit time, rather than live in the
    // search box: antd's own `tokenSeparators` tokenizer reads its search
    // text from the DOM as the user types, and controlling that text
    // ourselves to capitalize it live fought that tokenizer and silently
    // dropped tokens. Capitalizing the committed value instead — and only a
    // value that's genuinely new (neither a known option nor already part
    // of the previous selection, so an existing tag isn't re-capitalized on
    // every later, unrelated change) — sidesteps that entirely.
    //
    // Tags mode only: combobox mode's onChange fires on every keystroke
    // (rc-select's onInternalSearch calls triggerChange there directly, not
    // just on commit), and this wrapper has no caret-preservation the way
    // TextInput/TextArea do — capitalizing live would jump the caret. No
    // call site uses combobox mode today; this keeps it that way until one
    // does and this gets the same caret handling.
    const capitalizeCommittedValue = (value: unknown) => {
      if (!capitalizesFreeText || disableAutoCapitalize) return value
      const previous = new Set(
        (Array.isArray(props?.value) ? props.value : [props?.value]).map(String)
      )
      const capitalize = (entry: unknown) =>
        typeof entry === "string" &&
        !knownValues.has(entry) &&
        !previous.has(entry)
          ? capitalizeFirstLetter(entry)
          : entry
      return Array.isArray(value) ? value.map(capitalize) : capitalize(value)
    }

    const handleOnChange: SelectComponentProps["onChange"] = (
      value,
      option
    ) => {
      const finalValue = capitalizeCommittedValue(value)
      onChange?.(finalValue ?? null, option)
    }

    const combinedDropdownRender = (menu: React.ReactElement) => {
      const mobileWrapper = (content: React.ReactElement) => (
        <div className="[&>*>.rc-virtual-list-holder]:overscroll-none">
          {content}
        </div>
      )

      return props?.popupRender
        ? props.popupRender(mobileWrapper(menu))
        : mobileWrapper(menu)
    }

    useEffect(() => {
      const preventScroll = (event: WheelEvent | TouchEvent) => {
        if (open) {
          event.preventDefault()
        }
      }

      window.addEventListener("wheel", preventScroll, { passive: false })
      window.addEventListener("touchmove", preventScroll, { passive: false })

      return () => {
        window.removeEventListener("wheel", preventScroll)
        window.removeEventListener("touchmove", preventScroll)
      }
    }, [open])

    useEffect(() => {
      if (!isMobile) return
      return () => {
        document.body.style.overflow = "unset"
      }
    }, [isMobile])

    useEffect(() => {
      setOpen(!!props?.open)
    }, [props?.open])

    return (
      <Select
        // suffixIcon={
        //   <Icon type="chevron_down_small" style={{ pointerEvents: "none" }} />
        // }
        onChange={handleOnChange}
        // menuItemSelectedIcon={<Icon type="circle_check" />}
        ref={ref || null}
        className={cn(
          className,
          "select [&_.ant-select-selection-placeholder]:text-[#9E9E9E]",
          {
            "[&_.ant-select-clear]:scale-125": isMobile,
            "disabled-transition": isDisabledTransition
          }
        )}
        allowClear={allowClear}
        {...{
          ...(options ? { options: filteredOptions || [] } : {}),
          ...props,
          ...(isMobile
            ? {
                // getPopupContainer: (trigger) =>
                //   trigger?.parentNode || document.body,
                onOpenChange: handleOpenChange,
                clearIcon: (
                  <button className={"z-10"}>
                    <CloseCircleOutlined />
                  </button>
                ),
                popupRender: combinedDropdownRender
              }
            : {})
        }}
      />
    )
  }
)

SelectComponent.displayName = "Select"

export default SelectComponent
